import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import {
    PHONE_DISPLAY,
    PHONE_HREF,
    EMAIL,
    EMAIL_HREF,
    ADDRESS,
    HOURS,
    CANTONS,
    NAV_LINKS,
    LOGO_SRC,
} from "@/lib/constants";
import { track } from "@/lib/track";

const Footer = () => {
    return (
        <footer
            data-testid="site-footer"
            className="bg-[#1F1F1F] text-white"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                    {/* Col 1 - logo + tagline */}
                    <div>
                        <Link to="/" data-testid="footer-logo-link" className="inline-block bg-white p-3 rounded-lg">
                            <img
                                src={LOGO_SRC}
                                alt="Capital Automobile"
                                className="h-10 w-auto"
                            />
                        </Link>
                        <p className="mt-4 text-sm text-gray-300 leading-relaxed max-w-xs">
                            Rachat de votre voiture cash en 24h dans toute la Suisse romande.
                        </p>
                    </div>

                    {/* Col 2 - Contact */}
                    <div>
                        <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-4">
                            Contact
                        </h3>
                        <ul className="space-y-3 text-sm">
                            <li>
                                <a
                                    href={PHONE_HREF}
                                    data-testid="footer-phone-link"
                                    onClick={() =>
                                        track("phone_click", {
                                            location: "footer",
                                        })
                                    }
                                    className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors"
                                >
                                    <Phone className="w-4 h-4 mt-0.5 text-brand-red shrink-0" />
                                    <span>{PHONE_DISPLAY}</span>
                                </a>
                            </li>
                            <li>
                                <a
                                    href={EMAIL_HREF}
                                    data-testid="footer-email-link"
                                    className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors break-all"
                                >
                                    <Mail className="w-4 h-4 mt-0.5 text-brand-red shrink-0" />
                                    <span>{EMAIL}</span>
                                </a>
                            </li>
                            <li className="flex items-start gap-3 text-gray-300">
                                <MapPin className="w-4 h-4 mt-0.5 text-brand-red shrink-0" />
                                <span>{ADDRESS}</span>
                            </li>
                            <li className="flex items-start gap-3 text-gray-300">
                                <Clock className="w-4 h-4 mt-0.5 text-brand-red shrink-0" />
                                <span>{HOURS}</span>
                            </li>
                        </ul>
                    </div>

                    {/* Col 3 - Zones */}
                    <div>
                        <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-4">
                            Zones desservies
                        </h3>
                        <ul className="space-y-2 text-sm">
                            {CANTONS.map((c) => (
                                <li key={c.slug}>
                                    <Link
                                        to={`/rachat-voiture-${c.slug}`}
                                        data-testid={`footer-canton-link-${c.slug}`}
                                        className="text-gray-300 hover:text-brand-red transition-colors"
                                    >
                                        Rachat voiture {c.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Col 4 - Liens */}
                    <div>
                        <h3 className="text-sm font-bold uppercase tracking-wider text-white mb-4">
                            Liens
                        </h3>
                        <ul className="space-y-2 text-sm">
                            {NAV_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-nav-link-${l.to.replace(/[^a-z0-9]+/gi, "-")}`}
                                        className="text-gray-300 hover:text-brand-red transition-colors"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                            <li>
                                <Link
                                    to="/mentions-legales"
                                    data-testid="footer-mentions-legales-link"
                                    className="text-gray-300 hover:text-brand-red transition-colors"
                                >
                                    Mentions légales
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/politique-confidentialite"
                                    data-testid="footer-privacy-link"
                                    className="text-gray-300 hover:text-brand-red transition-colors"
                                >
                                    Politique de confidentialité
                                </Link>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    data-testid="footer-manage-cookies"
                                    onClick={() => {
                                        try {
                                            window.dispatchEvent(
                                                new Event("capitalauto-open-cookie-prefs"),
                                            );
                                        } catch (_) {
                                            /* no-op */
                                        }
                                    }}
                                    className="text-gray-300 hover:text-brand-red transition-colors text-left"
                                >
                                    Gérer les cookies
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-12 pt-8 border-t border-white/10 text-center md:text-left">
                    <p className="text-xs text-gray-400" data-testid="footer-copyright">
                        © 2026 Capital Automobile — Tous droits réservés.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
